import React, { useState, useEffect, useCallback, useRef } from "react";
import { NavLink, useLocation } from "react-router-dom";
import { SidebarData } from "../Sidebar/SidebarData";
import { INDEX_URL, CUST, icons, logos, FLAVOR, images } from "../../helpers/constants";
import LanguagePicker from "../LanguagePicker";
import { useTranslation } from "react-i18next";
import { Collapse } from "@mui/material";
import * as request from "../../services/FetchService";
import InAppModal from "../Modals/InAppModal";
import { AnimatePresence, motion } from "framer-motion";
import { useSidebarContext } from "./SiderbarContext";
import { toggleTheme } from "../../assets/css/theme";

const fadeIn = {
	initial: { opacity: 0, y: 8 },
	animate: { opacity: 1, y: 0, transition: { duration: 0.25 } },
	exit: { opacity: 0, y: 8, transition: { duration: 0.2 } },
};

const SidebarMobile = ({}) => {
	const { t } = useTranslation();
	const location = useLocation();
	const { topbarContent, actionbarContent } = useSidebarContext();
	const [showBurger, setShowBurger] = useState(false);
	const [showModal, setShowModal] = useState(false);
	const [pageName, setPageName] = useState("");

	const [notifications, setNotifications] = useState({
		alarmicon: false,
		messageicon: false,
		startupmessage: { show: false, newuser: false },
	});

	// ---- Polling infrastructure (same pattern as desktop) ----
	const intervalRef = useRef(null);
	const inFlightRef = useRef(false);
	const [isVisible, setIsVisible] = useState(() => !document.hidden);
	const [tema, setTema] = useState(
		() => document.documentElement.getAttribute("data-theme") || "light"
	);
	const getNewMessage = useCallback(async () => {
		try {
			const response = await request.get("account/messagenotification/check");
			if (response && response.data && response.data.successful) {
				const newData = response.data.data || {};
				setNotifications((prev) => ({
					...prev,
					alarmicon: newData.alarmicon,
					messageicon: newData.messageicon,
					startupmessage: {
						...prev.startupmessage,
						show: newData.startupmessage && "show" in newData.startupmessage ? newData.startupmessage.show : prev.startupmessage.show,
						newuser: newData.startupmessage && "newuser" in newData.startupmessage ? newData.startupmessage.newuser : prev.startupmessage.newuser,
					},
				}));
			}
		} catch (e) {
			// Optional: log/backoff
			// console.error(e);
		}
	}, []);

	// Prevent overlapping requests
	const safeFetch = useCallback(async () => {
		if (inFlightRef.current) return;
		inFlightRef.current = true;
		try {
			await getNewMessage();
		} finally {
			inFlightRef.current = false;
		}
	}, [getNewMessage]);

	// Track tab visibility
	useEffect(() => {
		const onVisibility = () => setIsVisible(!document.hidden);
		document.addEventListener("visibilitychange", onVisibility);
		return () => document.removeEventListener("visibilitychange", onVisibility);
	}, []);

	// Single authoritative polling effect: pause when tab hidden or modal open
	useEffect(() => {
		const shouldPoll = isVisible && !showModal;

		// Clear any existing interval before deciding
		if (intervalRef.current) {
			clearInterval(intervalRef.current);
			intervalRef.current = null;
		}

		if (shouldPoll) {
			// Immediate refresh on resume
			safeFetch();
			// Then poll every 60s
			intervalRef.current = setInterval(safeFetch, 60000);
		}

		// Cleanup on dependency change/unmount
		return () => {
			if (intervalRef.current) {
				clearInterval(intervalRef.current);
				intervalRef.current = null;
			}
		};
	}, [isVisible, showModal, safeFetch]);

	// One-off: clear alarm dot when visiting /events
	useEffect(() => {
		if (location.pathname === "/events") {
			setNotifications((prev) => ({ ...prev, alarmicon: false }));
		}
	}, [location.pathname]);

	const handleModal = () => {
		setShowBurger(false);
		setShowModal(true);
	};

	useEffect(() => {
		document.documentElement.classList.toggle("no-scroll", showBurger);
	});

	const findPageName = () => {
		const currPage = SidebarData.find((page) => page.path === location.pathname) ?? "";
		setPageName(currPage.title ?? "");
	};

	useEffect(() => {
		findPageName();
	}, [location.pathname]);

	const onToggleTheme = () => {
		const curr = toggleTheme()
		setTema(curr)
	}
	const darkmodeIconSrc = tema === "dark" ? icons.sun : icons.moon;
	

	return (
		<>
			<motion.nav
				className={`fixed-top glass-effect rounded32 d-flex actionbar flex-column scrollable-y`}
				style={{ zIndex: 1040, willChange: "height", overflow: "hidden", boxSizing: "border-box" }}
				initial={false}
				animate={{ height: showBurger ? "calc(100dvh - 40px)" : actionbarContent ? "" : "58px" }}
				transition={{ duration: 0.5, ease: [0.25, 1, 0.25, 1] }}
				aria-expanded={showBurger}
			>
				{/* Top bar row (swap blocks completely to avoid title/icon conflicts) */}
				<motion.div className="d-flex justify-self-center align-items-center justify-content-between position-relative py-2 mx-3">
					{!topbarContent &&
					<>
						<div className="cursor-pointer round-bg bg-white menu-button d-flex align-items-center justify-content-center" onClick={() => setShowBurger(!showBurger)}>
							<AnimatePresence mode="wait" initial={false}>
								{showBurger ? (
									<motion.img key="close" src={icons.close} alt="close" className="size_20 primary-icon" initial={{ opacity: 1, rotate: 0 }} animate={{ opacity: 1, rotate: 90 }} exit={{ opacity: 1, rotate: 180 }} transition={{ duration: 0.2 }} />
								) : (
									<motion.img key="hamburger" src={icons.hamburger} alt="hamburger" className="size_20 primary-icon" initial={{ opacity: 1, rotate: -45 }} animate={{ opacity: 1, rotate: 0 }} exit={{ opacity: 1, rotate: 45 }} transition={{ duration: 0.2 }} />
								)}
							</AnimatePresence>
						</div>
						<AnimatePresence mode="wait">
							{!showBurger ? (
								<motion.h4 key="title" className="mega-center m-0" initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }} transition={{ duration: 0.2 }}>
									{t(pageName)}
								</motion.h4>
							) : (
								<motion.img key="burger-logo"  className="mega-center m-0 w-25" src={logos.big}  initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }} transition={{ duration: 0.2 }} />
							)}
						</AnimatePresence>
						<div />
					</>
					}
					{topbarContent?.left === "burger"
						? (	
						<div className="cursor-pointer round-bg bg-white menu-button d-flex align-items-center justify-content-center" onClick={() => setShowBurger(!showBurger)}>
							<AnimatePresence mode="wait" initial={false}>
								{showBurger ? (
									<motion.img key="close" src={icons.close} alt="close" className="size_20 primary-icon" initial={{ opacity: 1, rotate: 0 }} animate={{ opacity: 1, rotate: 90 }} exit={{ opacity: 1, rotate: 180 }} transition={{ duration: 0.2 }} />
								) : (
									<motion.img key="hamburger" src={icons.hamburger} alt="hamburger" className="size_20 primary-icon" initial={{ opacity: 1, rotate: -45 }} animate={{ opacity: 1, rotate: 0 }} exit={{ opacity: 1, rotate: 45 }} transition={{ duration: 0.2 }} />
								)}
							</AnimatePresence>
						</div>
						)
						: topbarContent?.left || <div />
					}
					{!showBurger ?
					<>
						{topbarContent?.center || <div />}
						{topbarContent?.right || <div />}
					</>
					:
					<motion.img key="burger-logo"  className="mega-center m-0 w-25" src={logos.big}  initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }} transition={{ duration: 0.2 }} />

					
					}
				

					{/* Page title fades out when burger menu opens */}

				</motion.div>
				<AnimatePresence initial={false}>
					{actionbarContent && (actionbarContent)}
				</AnimatePresence>
				{/* Expanding content area (only visible when expanded) */}
				<AnimatePresence initial={false}>
					{showBurger && (
						<motion.div key="menu" className="flex-grow-1 position-relative" {...fadeIn}>
							{/* Scrollable content inside the glass nav */}
							<div className="h-100 overflow-auto">
								<div className="d-flex p-4 h-100">
									<div className="d-flex flex-column w-100 position-relative">
										{SidebarData.map((item, index) => (
											<NavLink key={index} className="position-relative d-flex justify-content-start my-2 nav-item-mobile" to={item.path} onClick={() => setShowBurger(false)}>
												{/* <img src={item.icon_inactive} className="cursor-pointer primary-icon" alt="..." /> */}
												{item.path === "/events" && !notifications?.alarmicon && <span className="position-absolute dot bg-offline top-0 mt-0 start-0 ms-2" />}
												{item.path === "/messages" && notifications?.messageicon && <span className="position-absolute dot bg-offline top-0 mt-0 start-0 ms-2" />}

												<h1 className={`ms-3 ${location?.pathname === item.path ? "gradient" : "standard"}`}>{t(item.title)}</h1>
												{/* <img src={icons.chevron_right} className="cursor-pointer primary-icon" alt="..." /> */}
											</NavLink>
										))}
										{/* Pinned bottom language picker */}
										<div className="ms-3 mt-auto cursor-pointer rounded d-flex justify-content-start align-items-center">
											<LanguagePicker />
											{process.env.REACT_APP_ASSETS === "swetrack" &&
												<img onClick={onToggleTheme} src={darkmodeIconSrc} className={`grey-blue-icon icon-cell position-relative cursor-pointer size_20`} alt="..." />
											}
										</div>
									</div>
								</div>
							</div>
						</motion.div>
					)}
				</AnimatePresence>
			</motion.nav>

			<InAppModal show={showModal} onClose={() => setShowModal((s) => !s)} />
		</>
	);
};

export default SidebarMobile;
